class ImageBannerSlideshow {
  constructor(container) {
    this.container = container;
    this.slides = container.querySelectorAll('.slideshow__slide');
    this.arrows = container.querySelectorAll('.slideshow__arrow');
    this.dots = container.querySelectorAll('.slideshow__dot');
    this.currentSlide = 0;
    this.isPlaying = false;
    this.autoplayTimer = null;
    
    console.log('ImageBannerSlideshow initialized:', {
      container: this.container,
      slidesFound: this.slides.length,
      arrowsFound: this.arrows.length,
      dotsFound: this.dots.length
    });
    
    // Get settings from data attributes
    this.autoplay = container.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(container.dataset.autoplaySpeed) * 1000 || 5000;
    this.showArrows = container.dataset.showArrows === 'true';
    this.showDots = container.dataset.showDots === 'true';
    
    // Always initialize, even with single slide
    this.init();
    
    // Show first slide immediately
    if (this.slides.length > 0) {
      console.log('Activating first slide');
      this.slides[0].setAttribute('data-active', 'true');
      this.slides[0].setAttribute('aria-hidden', 'false');
    }
  }
  
  init() {
    this.setupEventListeners();
    this.updateSlideVisibility();
    
    // Only setup autoplay if there are multiple slides
    if (this.autoplay && this.slides.length > 1) {
      this.startAutoplay();
    }
    
    // Only add interaction listeners if multiple slides
    if (this.slides.length > 1) {
      // Pause autoplay when user interacts
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => {
        if (this.autoplay) {
          this.startAutoplay();
        }
      });
      
      // Pause autoplay when page is not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else if (this.autoplay) {
          this.startAutoplay();
        }
      });
    }
  }
  
  setupEventListeners() {
    // Arrow navigation
    this.arrows.forEach(arrow => {
      arrow.addEventListener('click', (e) => {
        e.preventDefault();
        if (arrow.classList.contains('slideshow__arrow--prev')) {
          this.previousSlide();
        } else {
          this.nextSlide();
        }
      });
    });
    
    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(index);
      });
    });
    
    // Keyboard navigation
    this.container.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.previousSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          this.toggleAutoplay();
          break;
      }
    });
    
    // Touch/swipe support
    let startX = 0;
    let endX = 0;
    
    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });
    
    this.container.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      const diffX = startX - endX;
      
      // Minimum swipe distance
      if (Math.abs(diffX) > 50) {
        if (diffX > 0) {
          this.nextSlide();
        } else {
          this.previousSlide();
        }
      }
    }, { passive: true });
  }
  
  nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    this.updateSlideVisibility();
    this.resetAutoplayTimer();
  }
  
  previousSlide() {
    this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.updateSlideVisibility();
    this.resetAutoplayTimer();
  }
  
  goToSlide(index) {
    if (index >= 0 && index < this.slides.length) {
      this.currentSlide = index;
      this.updateSlideVisibility();
      this.resetAutoplayTimer();
    }
  }
  
  updateSlideVisibility() {
    // Update slides
    this.slides.forEach((slide, index) => {
      if (index === this.currentSlide) {
        slide.setAttribute('data-active', 'true');
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.removeAttribute('data-active');
        slide.setAttribute('aria-hidden', 'true');
      }
    });
    
    // Update dots
    this.dots.forEach((dot, index) => {
      if (index === this.currentSlide) {
        dot.classList.add('slideshow__dot--active');
        dot.setAttribute('aria-pressed', 'true');
      } else {
        dot.classList.remove('slideshow__dot--active');
        dot.setAttribute('aria-pressed', 'false');
      }
    });
    
    // Announce slide change to screen readers
    this.announceSlideChange();
  }
  
  startAutoplay() {
    if (this.slides.length <= 1) return;
    
    this.isPlaying = true;
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }
  
  stopAutoplay() {
    this.isPlaying = false;
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  resetAutoplayTimer() {
    if (this.autoplay && this.isPlaying) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }
  
  toggleAutoplay() {
    if (this.isPlaying) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  }
  
  announceSlideChange() {
    // Create or update live region for screen readers
    let liveRegion = this.container.querySelector('.slideshow__live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'slideshow__live-region visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      this.container.appendChild(liveRegion);
    }
    
    const currentSlideNumber = this.currentSlide + 1;
    const totalSlides = this.slides.length;
    liveRegion.textContent = `Slide ${currentSlideNumber} of ${totalSlides}`;
  }
  
  // Public method to destroy the slideshow
  destroy() {
    this.stopAutoplay();
    
    // Remove event listeners
    this.arrows.forEach(arrow => {
      arrow.replaceWith(arrow.cloneNode(true));
    });
    
    this.dots.forEach(dot => {
      dot.replaceWith(dot.cloneNode(true));
    });
    
    // Clean up live region
    const liveRegion = this.container.querySelector('.slideshow__live-region');
    if (liveRegion) {
      liveRegion.remove();
    }
  }
}

// Initialize all slideshows on the page
function initSlideshows() {
  const slideshows = document.querySelectorAll('.slideshow');
  const instances = [];
  
  slideshows.forEach(slideshow => {
    const instance = new ImageBannerSlideshow(slideshow);
    instances.push(instance);
  });
  
  return instances;
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSlideshows);
} else {
  initSlideshows();
}

// Handle dynamic content loading (Shopify sections)
document.addEventListener('shopify:section:load', (event) => {
  const slideshow = event.target.querySelector('.slideshow');
  if (slideshow) {
    new ImageBannerSlideshow(slideshow);
  }
});

// Export for manual initialization
window.ImageBannerSlideshow = ImageBannerSlideshow;